import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { RoleBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Search, Plus, Minus, Coins, ShieldCheck, ShieldX, CreditCard, Lock } from "lucide-react";

export default function Users() {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const qc = useQueryClient();

  const { data: users = [] } = useQuery({
    queryKey: ["users", query],
    queryFn: async () => (await api.get(`/admin/users`, { params: { search: query } })).data,
  });

  const { data: detail } = useQuery({
    queryKey: ["user", selected],
    queryFn: async () => (await api.get(`/admin/users/${selected}`)).data,
    enabled: !!selected,
  });

  const modify = async (action) => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) return toast.error("Ingresa una cantidad entera > 0");
    try {
      const { data } = await api.post(`/admin/users/${selected}/credits`, { action, amount: amt });
      toast.success(`Saldo: ${data.before} → ${data.after}`);
      setAmount("");
      qc.invalidateQueries({ queryKey: ["user", selected] });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const toggleAuth = async (authorized) => {
    try {
      await api.post(`/admin/users/${selected}/authorize`, { authorized });
      toast.success(authorized ? "Usuario autorizado" : "Acceso revocado");
      qc.invalidateQueries({ queryKey: ["user", selected] });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleCard = async (enabled) => {
    try {
      await api.post(`/admin/users/${selected}/card-payments`, { enabled });
      toast.success(enabled ? "Pago con tarjeta habilitado" : "Pago con tarjeta bloqueado");
      qc.invalidateQueries({ queryKey: ["user", selected] });
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Layout>
      <PageHeader title="Usuarios" subtitle="Búsqueda y administración de créditos" />
      <div className="p-8">
        <div className="flex gap-2 mb-5 max-w-xl">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input data-testid="user-search-input" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setQuery(search)}
              placeholder="Buscar por Telegram ID o username..."
              className="pl-9 bg-[#0D111A] border-[#1E293B] text-slate-100" />
          </div>
          <Button data-testid="user-search-btn" onClick={() => setQuery(search)}
            className="bg-[#C2620E] hover:bg-[#FF8A2B] text-white">Buscar</Button>
        </div>

        <div className="rounded-lg border overflow-hidden" style={{ borderColor: "#1E293B" }}>
          <table className="w-full text-sm" data-testid="users-table">
            <thead style={{ backgroundColor: "#0D111A" }}>
              <tr className="text-left text-xs text-slate-500 uppercase">
                <th className="px-4 py-3">Telegram ID</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Créditos</th>
                <th className="px-4 py-3">Trabajos</th>
                <th className="px-4 py-3">Rol</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Acceso</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.telegram_id} data-testid={`user-row-${u.telegram_id}`}
                  onClick={() => setSelected(u.telegram_id)}
                  className="border-t cursor-pointer hover:bg-[#0D111A] transition-colors"
                  style={{ borderColor: "#1E293B" }}>
                  <td className="px-4 py-3 font-mono text-slate-200">{u.telegram_id}</td>
                  <td className="px-4 py-3 text-slate-400">{u.username || "—"}</td>
                  <td className="px-4 py-3 font-mono text-amber-400">{u.credits}</td>
                  <td className="px-4 py-3 text-slate-400">{u.jobs_completed || 0}</td>
                  <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-4 py-3 text-emerald-400 text-xs">{u.status}</td>
                  <td className="px-4 py-3 text-xs">
                    {u.authorized
                      ? <span className="text-emerald-400">✅ Sí</span>
                      : <span className="text-rose-400">🚫 No</span>}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">Sin usuarios</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="bg-[#0D111A] border-l border-[#1E293B] text-slate-100 overflow-y-auto w-full sm:max-w-lg" data-testid="user-detail-sheet">
          <SheetHeader>
            <SheetTitle className="text-slate-100 font-heading">Detalle de usuario</SheetTitle>
            <SheetDescription className="text-slate-500">Créditos, transacciones e información del usuario.</SheetDescription>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-5">
              <div className="rounded-lg border p-4" style={{ borderColor: "#1E293B", backgroundColor: "#111827" }}>
                <div className="font-mono text-lg text-slate-100">{detail.user.telegram_id}</div>
                <div className="text-sm text-slate-400">{detail.user.first_name} {detail.user.last_name} · @{detail.user.username || "—"}</div>
                <div className="flex items-center gap-2 mt-3">
                  <Coins className="w-5 h-5 text-amber-400" />
                  <span className="font-heading text-2xl font-bold text-amber-400">{detail.user.credits}</span>
                  <span className="text-xs text-slate-500">créditos · {detail.user.reserved || 0} reservados</span>
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ borderColor: "#1E293B", backgroundColor: "#111827" }}>
                <div className="text-xs text-slate-500 uppercase mb-2">Modificar créditos</div>
                <Input data-testid="credit-amount-input" value={amount} type="number"
                  onChange={(e) => setAmount(e.target.value)} placeholder="Cantidad (entero)"
                  className="bg-[#0D111A] border-[#1E293B] text-slate-100 mb-3" />
                <div className="flex gap-2">
                  <Button data-testid="add-credits-btn" onClick={() => modify("add")}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white">
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                  <Button data-testid="deduct-credits-btn" onClick={() => modify("deduct")}
                    className="flex-1 bg-rose-600 hover:bg-rose-500 text-white">
                    <Minus className="w-4 h-4 mr-1" /> Retirar
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ borderColor: "#1E293B", backgroundColor: "#111827" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Acceso al bot (privado)</div>
                    <div className="text-sm mt-1">{detail.user.authorized
                      ? <span className="text-emerald-400">✅ Autorizado</span>
                      : <span className="text-rose-400">🚫 No autorizado</span>}</div>
                  </div>
                  {detail.user.authorized
                    ? <Button data-testid="revoke-access-btn" onClick={() => toggleAuth(false)}
                        className="bg-rose-600 hover:bg-rose-500 text-white"><ShieldX className="w-4 h-4 mr-1" /> Revocar</Button>
                    : <Button data-testid="authorize-access-btn" onClick={() => toggleAuth(true)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"><ShieldCheck className="w-4 h-4 mr-1" /> Autorizar</Button>}
                </div>
              </div>

              <div className="rounded-lg border p-4" style={{ borderColor: "#1E293B", backgroundColor: "#111827" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase">Pago con tarjeta (Stripe)</div>
                    <div className="text-sm mt-1">{detail.user.card_payments_enabled
                      ? <span className="text-emerald-400">💳 Habilitado</span>
                      : <span className="text-rose-400">🔒 Bloqueado</span>}</div>
                  </div>
                  {detail.user.card_payments_enabled
                    ? <Button data-testid="card-payments-off-btn" onClick={() => toggleCard(false)}
                        className="bg-rose-600 hover:bg-rose-500 text-white"><Lock className="w-4 h-4 mr-1" /> Quitar</Button>
                    : <Button data-testid="card-payments-on-btn" onClick={() => toggleCard(true)}
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"><CreditCard className="w-4 h-4 mr-1" /> Habilitar</Button>}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-500 uppercase mb-2">Historial de transacciones</div>
                <div className="space-y-1.5 max-h-80 overflow-y-auto">
                  {detail.transactions.map((t) => (
                    <div key={t.id} className="flex items-center justify-between text-xs px-3 py-2 rounded"
                      style={{ backgroundColor: "#111827" }}>
                      <span className="font-mono text-slate-400">{t.type}</span>
                      <span className={`font-mono ${t.amount >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {t.type === "DEDUCT" || t.type === "RESERVE" ? "-" : "+"}{t.amount} → {t.balance_after}
                      </span>
                      <span className="text-slate-600">{t.created_at?.slice(0, 10)}</span>
                    </div>
                  ))}
                  {detail.transactions.length === 0 && <div className="text-slate-500 text-xs">Sin movimientos</div>}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Layout>
  );
}
